import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  Col,
  Row,
  Select,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  AppstoreOutlined,
  BankOutlined,
  CheckCircleOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import CopyableText from "common/CopyableText";
import RichTextEditor from "components/common/RichTextEditor";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import {
  fetchWorkspace,
  updateWorkspaceOrganization,
} from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import {
  organizationName,
  formatDateTime,
  readable,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";

const { Text, Title } = Typography;

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const trimValue = (value) => String(value || "").trim();

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const entityContact = (entity) => {
  const contact = entity?.contactInformation || {};
  return [
    contact.primaryEmail || entity?.email,
    contact.primaryPhone || entity?.phoneNumber || entity?.phone,
    contact.websiteUrl || entity?.websiteUrl || entity?.website,
  ]
    .filter(Boolean)
    .join(" • ");
};

const locationText = (entity) => {
  const location = entity?.locationInformation || {};
  return [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.state,
    location.zipCode,
    location.country,
  ]
    .filter(Boolean)
    .join(", ");
};

const WorkspaceOrganizationPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshWorkspaces } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [account, setAccount] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [changeOrganizationOpen, setChangeOrganizationOpen] = useState(false);
  const [organizationTab, setOrganizationTab] = useState("select");
  const [createOrganizationOpen, setCreateOrganizationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm] = Form.useForm();

  const locationTypeOptions = useMemo(
    () =>
      locationTypes.map((type) => ({
        value: type.id,
        label: type.label || type.code || type.id,
      })),
    [locationTypes],
  );

  const loadData = useCallback(async () => {
    if (!workspaceId || !actorId) return;

    try {
      setLoading(true);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount || {});
      setSelectedOrganizationId(nextAccount?.organizationId || "");

      if (isPersonalAccount(nextAccount)) {
        setOrganization(null);
        setOrganizations([]);
        setLocationTypes([]);
        setChangeOrganizationOpen(false);
        setCreateOrganizationOpen(false);
        navigate(paths.workspaceDetails(workspaceId), { replace: true });
        return;
      }

      const [entityPage, typePage] = await Promise.all([
        olreadiaService.organizations.listForUser(actorId, {
          page: 0,
          size: 50,
        }),
        olreadiaService.businessLocationTypes.list({ active: true }),
      ]);

      const nextOrganizations = pageContent(entityPage);
      setOrganizations(nextOrganizations);
      setLocationTypes(pageContent(typePage));

      if (nextAccount?.organizationId) {
        const entity = await olreadiaService.organizations.get(nextAccount.organizationId);
        setOrganization(entity || {});
      } else {
        setOrganization(null);
      }
    } catch (error) {
      message.error(error?.message || "Failed to load organization details.");
    } finally {
      setLoading(false);
    }
  }, [actorId, navigate, workspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const performLinkOrganization = async (organizationId) => {
    const nextOrganizationId = trimValue(organizationId);
    if (!nextOrganizationId) {
      throw new Error("Select an organization.");
    }

    const updatedAccount = await updateWorkspaceOrganization({
      actorId,
      workspaceId: workspaceId,
      organizationId: nextOrganizationId,
    });
    setAccount(updatedAccount || { ...account, organizationId: nextOrganizationId });
    await refreshWorkspaces();
    await loadData();
    return updatedAccount;
  };

  const linkOrganization = async (organizationId) => {
    try {
      setSavingLink(true);
      await performLinkOrganization(organizationId);
      setChangeOrganizationOpen(false);
      setCreateOrganizationOpen(false);
      message.success("Organization linked.");
    } catch (error) {
      message.error(error?.message || "Failed to link organization.");
    } finally {
      setSavingLink(false);
    }
  };

  const createAndLinkOrganization = async (values) => {
    try {
      setCreating(true);
      const payload = {
        name: trimValue(values.name),
        description: trimValue(values.description),
        ein: trimValue(values.ein),
        businessLocationTypeId: values.businessLocationTypeId,
        status: "DRAFT",
        contactInformation: {
          primaryEmail: trimValue(values.primaryEmail),
          primaryPhone: trimValue(values.primaryPhone),
          websiteUrl: trimValue(values.websiteUrl),
          contactPersonName: trimValue(values.contactPersonName),
        },
      };

      const entity = await olreadiaService.organizations.create(payload, actorId);
      if (!entity?.id) {
        throw new Error("Created organization id is missing.");
      }

      setSavingLink(true);
      await performLinkOrganization(entity.id);
      createForm.resetFields();
      setCreateOrganizationOpen(false);
      message.success("Organization created and linked.");
    } catch (error) {
      message.error(error?.message || "Failed to create organization.");
    } finally {
      setSavingLink(false);
      setCreating(false);
    }
  };

  const linked = Boolean(account?.organizationId);
  const personalWorkspace = isPersonalAccount(account);

  const openCreateOrganization = () => {
    createForm.resetFields();
    setCreateOrganizationOpen(true);
    setOrganizationTab("create");
  };

  const closeCreateOrganization = () => {
    createForm.resetFields();
    setCreateOrganizationOpen(false);
  };

  const renderCreateOrganizationPrompt = (description) => (
    <Card
      style={{
        borderRadius: 8,
        borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
        background: `linear-gradient(135deg, #ffffff 0%, ${OLIVEEB_COLORS.bgSoft} 100%)`,
      }}
    >
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={description} />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateOrganization}
          style={{
            borderColor: OLIVEEB_COLORS.oliveebGreen,
            background: OLIVEEB_COLORS.oliveebGreen,
            fontWeight: 700,
          }}
        >
          Create Organization
        </Button>
      </Space>
    </Card>
  );

  const renderCreateOrganizationForm = () => (
    <Card
      size="small"
      style={{
        borderRadius: 8,
        borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
        background: `linear-gradient(135deg, #ffffff 0%, ${OLIVEEB_COLORS.bgSoft} 58%, ${OLIVEEB_COLORS.oliveebBlueSoft} 100%)`,
        boxShadow: "0 14px 34px rgba(0, 64, 56, 0.10)",
      }}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <div>
          <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue }}>
            New Organization
          </Text>
          <Text style={{ display: "block", color: OLIVEEB_COLORS.textMuted, fontSize: 12 }}>
            Create a lightweight organization profile and link it to this workspace.
          </Text>
        </div>
        <Form
          form={createForm}
          layout="vertical"
          requiredMark={false}
          onFinish={createAndLinkOrganization}
        >
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="Organization name"
                rules={[{ required: true, message: "Enter organization name." }]}
              >
                <Input placeholder="Organization name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="businessLocationTypeId"
                label="Location type"
                rules={[{ required: true, message: "Select an organization location type." }]}
              >
                <Select
                  showSearch
                  placeholder="Select type"
                  loading={loading}
                  optionFilterProp="label"
                  options={locationTypeOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="primaryEmail" label="Primary email">
                <Input placeholder="organization@example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="primaryPhone" label="Primary phone">
                <Input placeholder="+1..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="ein" label="EIN">
                <Input placeholder="Optional EIN" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="websiteUrl" label="Website">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="contactPersonName" label="Contact person">
                <Input placeholder="Contact name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="description" label="Description">
                <RichTextEditor height="150px" placeholder="Optional description" />
              </Form.Item>
            </Col>
          </Row>
          <Space wrap>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              loading={creating || savingLink}
              style={{
                borderColor: OLIVEEB_COLORS.oliveebGreen,
                background: OLIVEEB_COLORS.oliveebGreen,
                fontWeight: 700,
              }}
            >
              Create and Link
            </Button>
            <Button onClick={closeCreateOrganization}>Cancel</Button>
          </Space>
        </Form>
      </Space>
    </Card>
  );

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<ShopOutlined />}
          title="Organization"
          subtitle="Workspace organization identity, payment owner, and organization link"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            style={workspaceButtonStyles.back}
            onClick={() => navigate(paths.workspaceDetails(workspaceId))}
          >
            Back
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {loading && !account ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !account ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card style={workspaceStyles.softCard}>
            <Space size={12} align="start">
              <span
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  display: "grid",
                  placeItems: "center",
                  background: workspaceStyles.iconBubble.background,
                  color: workspaceStyles.iconBubble.color,
                  flex: "0 0 auto",
                }}
              >
                <AppstoreOutlined />
              </span>
              <div>
                <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Workspace</Text>
                <Title level={4} style={{ margin: 0, color: OLIVEEB_COLORS.oliveebDarkBlue }}>
                  {account.name || "Workspace"}
                </Title>
                <Text type="secondary">
                  {readable(account.product, "Product")} • {readable(account.accountType, "Workspace type")}
                </Text>
              </div>
            </Space>
          </Card>

          {personalWorkspace ? (
            <Card style={workspaceStyles.softCard}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Personal workspaces do not use organizations"
              >
                <Button
                  icon={<ArrowLeftOutlined />}
                  style={workspaceButtonStyles.back}
                  onClick={() => navigate(paths.workspaceDetails(workspaceId))}
                >
                  Back to Workspace
                </Button>
              </Empty>
            </Card>
          ) : linked ? (
            <>
              <Card
                title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Linked Organization</Text>}
                extra={
                  <Button
                    icon={<EditOutlined />}
                    style={{
                      borderColor: OLIVEEB_COLORS.oliveebBlue,
                      color: OLIVEEB_COLORS.oliveebBlue,
                      fontWeight: 700,
                    }}
                    onClick={() => {
                      setSelectedOrganizationId(account.organizationId || "");
                      setChangeOrganizationOpen(true);
                    }}
                  >
                    Change Organization
                  </Button>
                }
                style={{ borderRadius: 8 }}
              >
                {loading && !organization ? (
                  <Skeleton active paragraph={{ rows: 6 }} />
                ) : organization ? (
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <div className="d-flex align-items-start gap-3 flex-wrap">
                      <span
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 8,
                          display: "grid",
                          placeItems: "center",
                          background: OLIVEEB_COLORS.greenSoft2,
                          color: OLIVEEB_COLORS.oliveebDarkGreen,
                          fontSize: 20,
                        }}
                      >
                        <BankOutlined />
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <Title level={4} style={{ margin: 0 }}>
                          {organizationName(organization, account.organizationId)}
                        </Title>
                        {entityContact(organization) ? (
                          <Text type="secondary" style={{ wordBreak: "break-word" }}>
                            {entityContact(organization)}
                          </Text>
                        ) : null}
                        <Space wrap size={[4, 4]} style={{ marginTop: 8 }}>
                          {organization.status ? (
                            <Tag color="green">{readable(organization.status)}</Tag>
                          ) : null}
                          {organization.businessLocationType ? (
                            <Tag color="blue">
                              {organization.businessLocationType.label ||
                                organization.businessLocationType.code}
                            </Tag>
                          ) : null}
                        </Space>
                      </div>
                    </div>

                    <Descriptions
                      bordered
                      size="small"
                      column={{ xs: 1, md: 2 }}
                      items={[
                        {
                          key: "id",
                          label: "Organization ID",
                          children: organization.id ? (
                            <CopyableText
                              value={organization.id}
                            />
                          ) : (
                            account.organizationId
                          ),
                        },
                        {
                          key: "ein",
                          label: "EIN",
                          children: organization.ein || "-",
                        },
                        {
                          key: "location",
                          label: "Location",
                          children: locationText(organization) || "-",
                        },
                        {
                          key: "created",
                          label: "Created",
                          children: formatDateTime(organization.createdAt) || "-",
                        },
                        {
                          key: "updated",
                          label: "Updated",
                          children: formatDateTime(organization.updatedAt) || "-",
                        },
                      ]}
                    />
                  </Space>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Organization details are not available" />
                )}
              </Card>

              {changeOrganizationOpen ? (
                <Card
                  title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Change Organization</Text>}
                  style={{ borderRadius: 8, ...workspaceStyles.highlightedCard }}
                >
                  <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <Alert
                      type="info"
                      showIcon
                      message="Select another organization linked to your user."
                    />
                    {organizations.length ? (
                      <>
                        <Select
                          showSearch
                          placeholder="Select organization"
                          value={selectedOrganizationId || undefined}
                          onChange={setSelectedOrganizationId}
                          optionFilterProp="label"
                          style={{ width: "100%" }}
                          options={organizations.map((entity) => ({
                            value: entity.id,
                            label: organizationName(entity, entity.id),
                          }))}
                        />
                        <Space wrap>
                          <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            loading={savingLink}
                            onClick={() => linkOrganization(selectedOrganizationId)}
                            style={{
                              borderColor: OLIVEEB_COLORS.oliveebGreen,
                              background: OLIVEEB_COLORS.oliveebGreen,
                              fontWeight: 700,
                            }}
                          >
                            Update Organization
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedOrganizationId(account.organizationId || "");
                              setChangeOrganizationOpen(false);
                            }}
                          >
                            Cancel
                          </Button>
                        </Space>
                      </>
                    ) : (
                      createOrganizationOpen
                        ? renderCreateOrganizationForm()
                        : renderCreateOrganizationPrompt(
                            "No organizations linked to your user were found",
                          )
                    )}
                  </Space>
                </Card>
              ) : null}
            </>
          ) : (
            <>
              <Alert
                type="warning"
                showIcon
                message="This workspace is not linked to an organization."
                description="Select one of your existing organizations or create a new one and link it to this workspace."
              />

              <Tabs
                className="pa-top-tabs"
                activeKey={organizationTab}
                onChange={setOrganizationTab}
                items={[
                  {
                    key: "select",
                    label: "Select Existing",
                    children: (
                      <Card style={{ borderRadius: 8 }}>
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                          {organizations.length ? (
                            <>
                              <Select
                                showSearch
                                placeholder="Select organization"
                                value={selectedOrganizationId || undefined}
                                onChange={setSelectedOrganizationId}
                                optionFilterProp="label"
                                style={{ width: "100%" }}
                                options={organizations.map((entity) => ({
                                  value: entity.id,
                                  label: organizationName(entity, entity.id),
                                }))}
                              />
                              <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                loading={savingLink}
                                onClick={() => linkOrganization(selectedOrganizationId)}
                                style={{
                                  borderColor: OLIVEEB_COLORS.oliveebGreen,
                                  background: OLIVEEB_COLORS.oliveebGreen,
                                  fontWeight: 700,
                                }}
                              >
                                Link Organization
                              </Button>
                            </>
                          ) : (
                            renderCreateOrganizationPrompt(
                              "No organizations linked to your user were found",
                            )
                          )}
                        </Space>
                      </Card>
                    ),
                  },
                  {
                    key: "create",
                    label: "Add New",
                    children: (
                      createOrganizationOpen
                        ? renderCreateOrganizationForm()
                        : renderCreateOrganizationPrompt(
                            "Create an organization, then link it to this workspace.",
                          )
                    ),
                  },
                ]}
              />
            </>
          )}
        </Space>
      )}
    </div>
  );
};

export default WorkspaceOrganizationPage;
